// ./src/components/features/EventTickets/TicketReassignDrawer/TicketReassignDrawer.tsx
//
// Reassign an issued ticket to a different attendee. Pick an existing contact from
// the address book, or type a new person's details — the server materializes the
// person + adds them to the guest list (role guest) and repoints the ticket.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Stack, Group, Text, TextInput, Divider } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { EventTicket, TicketReassignGuestInput } from "@/utils/api";
import { AppDrawer, AppIcon } from "@/components/generic";
import { PersonPicker } from "@/components/features/EventGuests/PersonPicker";
import { getFormErrors } from "@/utils";
import { useFormSnapshot } from "@/hooks";

// The server refuses the whole attendee block under one name, `guest` ("A guest name (or an
// existing contact) is required", "Invalid contact", "Contact not found") — all three are about
// WHO the ticket goes to, so the message is folded onto the contact picker that leads the block
// instead of being left to the footer prefixed with a field name the user never sees.
const KNOWN_FORM_FIELDS = ["guest"] as const;

export interface TicketReassignDrawerProps {
  opened: boolean;
  onClose: () => void;
  ticket: EventTicket | null;
  /** Runs the reassign; resolves with the OpResult so the drawer can surface errors. */
  onConfirm: (guest: TicketReassignGuestInput) => Promise<OpResult>;
  inProgress?: boolean;
}

const TicketReassignDrawerComponent: React.FC<TicketReassignDrawerProps> = ({
  opened,
  onClose,
  ticket,
  onConfirm,
  inProgress = false,
}) => {
  const { t } = useTranslation(["ticketSales", "common"]);

  const [personId, setPersonId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<OpResult | null>(null);

  // Esc / a click outside / Cancel ask before discarding a picked contact or typed details (#123).
  const { isDirty, seedFrom, clear } = useFormSnapshot({ personId, firstName, lastName, email, phone });

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    setPersonId(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    seedFrom({ personId: null, firstName: "", lastName: "", email: "", phone: "" });
    setError(null);
  }, [opened, clear, seedFrom]);

  const canSubmit = !!personId || !!firstName.trim() || !!lastName.trim();

  const handleConfirm = useCallback(async () => {
    if (!canSubmit) {
      return;
    }

    setError(null);

    const result = await onConfirm({
      personId,
      firstName: firstName.trim() || null,
      lastName: lastName.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
    });

    if (result.code === OP_RESULT_CODES.OK) {
      onClose();

      return;
    }

    setError(result);
  }, [canSubmit, personId, firstName, lastName, email, phone, onConfirm, onClose]);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(error, KNOWN_FORM_FIELDS),
    [error]
  );

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("ticketSales:reassign.title", { defaultValue: "Reassign ticket" })}
      actionType="save-cancel"
      onConfirm={handleConfirm}
      confirmLabel={t("ticketSales:reassign.confirm", { defaultValue: "Reassign" })}
      confirmIcon="User"
      confirmDisabled={!canSubmit}
      loading={inProgress}
      errors={genericErrors}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      size="md"
      isDirty={isDirty}
    >
      <Stack gap="md">
        {ticket?.ticketCode && (
          <Text size="md" c="dimmed">
            {t("ticketSales:reassign.ticket", { defaultValue: "Ticket {{code}}", code: ticket.ticketCode })}
          </Text>
        )}

        <PersonPicker
          value={personId}
          onSelect={(person) => {
            setPersonId(person?.id ?? null);
            if (person) {
              setFirstName(person.firstName ?? "");
              setLastName(person.lastName ?? "");
              setEmail(person.email ?? "");
              setPhone(person.phone ?? "");
            }
          }}
          label={t("ticketSales:reassign.pickContact", { defaultValue: "Pick from address book" })}
          size="md"
          clearable
          error={fieldErrors.guest}
        />

        <Divider label={t("ticketSales:reassign.orNew", { defaultValue: "Or enter new attendee" })} labelPosition="center" />

        <Group grow align="flex-start">
          <TextInput
            label={t("ticketSales:sale.firstName", { defaultValue: "First name" })}
            value={firstName}
            onChange={(e) => {
              const v = e.currentTarget.value;
              setFirstName(v);
              setPersonId(null);
            }}
            size="md"
            data-autofocus
          />
          <TextInput
            label={t("ticketSales:sale.lastName", { defaultValue: "Last name" })}
            value={lastName}
            onChange={(e) => {
              const v = e.currentTarget.value;
              setLastName(v);
              setPersonId(null);
            }}
            size="md"
          />
        </Group>
        <Group grow align="flex-start">
          <TextInput
            label={t("ticketSales:sale.email", { defaultValue: "Email" })}
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            size="md"
          />
          <TextInput
            label={t("ticketSales:sale.phone", { defaultValue: "Phone" })}
            value={phone}
            onChange={(e) => setPhone(e.currentTarget.value)}
            size="md"
          />
        </Group>

        <Group gap={6} wrap="nowrap">
          <AppIcon iconName="User" size={14} />
          <Text size="sm" c="dimmed">
            {t("ticketSales:reassign.hint", {
              defaultValue: "The attendee is added to the guest list and the ticket is reassigned to them.",
            })}
          </Text>
        </Group>
      </Stack>
    </AppDrawer>
  );
};

export const TicketReassignDrawer = React.memo(TicketReassignDrawerComponent);
